import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const Breadcrumb = () => {
  const location = useLocation();
  const currentPath = location.pathname.replace("/", "");

  const breadcrumbNameMap = {
    // ==== General ====
    // "all-categories-one": "Categories",
    "product-list-one": "Items",
    "product-details": "Item Details",
    "cart-one": "Cart",
    "wishlist": "Wishlist",
    "account": "My Account",
    "checkout": "Checkout",
    "become-seller": "Become Seller",
    "seller-account": "Seller Account",
    "vendor": "Vendors List",
    "vendor-details": "Vendor Details",
    "blog": "Blogs",
    "blog-details": "Description",
    "contact": "Contact Us",

    // ==== User Dashboard ====
    "user": "User",
    "user/profile": "My Profile",
    "user/wallet": "My Wallet",
    "user/wallet/claim":"Claim Gift",
    "user/wallet/create":"Create Gift",
    "user/wallet/history":"Transaction History",
    "user/orders": "My Orders",
    "user/orders/details": "Order Details",
    "user/orders/invoice": "Invoice",
    "user/orders/replace": "Replace",
    "user/orders/track": "Track",

    // ==== Business Dashboard ====
    "business": "Business",
    "business/profile": "My Profile",
    "business/wallet": "My Wallet",
    "business/wallet/claim":"Claim Gift",
    "business/wallet/create":"Create Gift",
    "business/wallet/history":"Transaction History",
    "business/orders": "My Orders",
    "business/orders/details": "Order Details",
    "business/orders/invoice": "Invoice",
    "business/orders/replace": "Replace",
    "business/orders/track": "Track",
  };

  const hierarchyMap = {
    // "all-categories-one": ["all-categories-one"],
    "product-list-one": ["product-list-one"],
    "shop": ["product-list-one"],
    "product-details": ["product-list-one", "product-details"],
    "cart-one": ["product-list-one", "cart-one"],
    "wishlist": ["account", "wishlist"],
    "checkout": ["cart-one", "checkout"],
    "become-seller": ["become-seller"],
    "seller-form": ["become-seller", "seller-account"],
    "account": ["account"],
    "vendor": ["vendor"],
    "vendor-details": ["vendor", "vendor-details"],
    "blog": ["blog"],
    "blog-details": ["blog", "blog-details"],
    "contact": ["contact"],

    // ==== User Dashboard ====
    "user": ["user"],
    "user/profile": ["user", "user/profile"],
    "user/orders": ["user", "user/orders"],
    "user/orders/details": ["user", "user/orders", "user/orders/details"],
    "user/orders/invoice": ["user", "user/orders", "user/orders/details", "user/orders/invoice"],
    "user/orders/replace": ["user", "user/orders", "user/orders/details", "user/orders/replace"],
    "user/orders/track": ["user", "user/orders", "user/orders/details", "user/orders/track"],
    "user/wallet": ["user", "user/wallet"],
    "user/wallet/claim": ["user", "user/wallet", "user/wallet/claim"],
    "user/wallet/create": ["user", "user/wallet", "user/wallet/create"],
    "user/wallet/history": ["user", "user/wallet", "user/wallet/history"],

    // ==== Business Dashboard ====
    "business": ["business"],
    "business/profile": ["business", "business/profile"],
    "business/orders": ["business", "business/orders"],
    "business/orders/details": ["business", "business/orders", "business/orders/details"],
    "business/orders/invoice": ["business", "business/orders", "business/orders/details", "business/orders/invoice"],
    "business/orders/replace": ["business", "business/orders", "business/orders/details", "business/orders/replace"],
    "business/orders/track": ["business", "business/orders", "business/orders/details", "business/orders/track"],
    "business/wallet": ["business", "business/wallet"],
    "business/wallet/claim": ["business", "business/wallet", "business/wallet/claim"],
    "business/wallet/create": ["business", "business/wallet", "business/wallet/create"],
    "business/wallet/history": ["business", "business/wallet", "business/wallet/history"],
  };

  //  Dynamic route handling
  let effectivePath = currentPath;

  // Product details dynamic
if (/^product-details\/[0-9a-fA-F]{24}$/.test(currentPath)) {
  effectivePath = "product-details";
}

  // User dynamic
  if (/^user\/orders\/\d+$/.test(currentPath)) {
    effectivePath = "user/orders/details";
  } else if (/^user\/orders\/\d+\/invoice$/.test(currentPath)) {
    effectivePath = "user/orders/invoice";
  } else if (/^user\/orders\/\d+\/replace$/.test(currentPath)) {
    effectivePath = "user/orders/replace";
  } else if (/^user\/orders\/\d+\/track$/.test(currentPath)) {
    effectivePath = "user/orders/track";
  }

  // Business dynamic
  else if (/^business\/orders\/\d+$/.test(currentPath)) {
    effectivePath = "business/orders/details";
  } else if (/^business\/orders\/\d+\/invoice$/.test(currentPath)) {
    effectivePath = "business/orders/invoice";
  } else if (/^business\/orders\/\d+\/replace$/.test(currentPath)) {
    effectivePath = "business/orders/replace";
  } else if (/^business\/orders\/\d+\/track$/.test(currentPath)) {
    effectivePath = "business/orders/track";
  }

  const breadcrumbTrail = hierarchyMap[effectivePath] || [];


    const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      ...breadcrumbTrail.map((name, index) => ({
        "@type": "ListItem",
        "position": index + 2,
        "name": breadcrumbNameMap[name] || name.replace("-", " "),
        "item": `${window.location.origin}/${name}`
      }))
    ]
  };


  return (
     <>
    <Helmet>
    <script type="application/ld+json">
      {JSON.stringify(breadcrumbSchema)}
    </script>
  </Helmet>
    <div
      className="breadcrumb mb-0 py-4"
      style={{ background: "linear-gradient(90deg, #e54b24 0%, #F3FAF2 100%)" }}
    >
      <div className="container container-lg">
        <div className="breadcrumb-wrapper flex-between flex-wrap gap-16">
          <h6 className="mb-0 capitalize text-[#1E293B]">
            {breadcrumbTrail.length > 0
              ? breadcrumbNameMap[breadcrumbTrail[breadcrumbTrail.length - 1]]
              : "Home"}
          </h6>

          <ul className="flex-align gap-8 flex-wrap">
            <li className="text-sm">
              <Link to="/" className="text-gray-900 flex-align gap-8 hover-text-main-600">
                <i className="ph ph-house" />
                Home
              </Link>
            </li>

            {breadcrumbTrail.map((name, index) => {
              const isLast = index === breadcrumbTrail.length - 1;
              const label = breadcrumbNameMap[name] || name.replace("-", " ");
              const routeTo = "/" + name;

              return (
                <span key={index} className="flex items-center gap-2">
                  <i className="ph ph-caret-right" />
                  {isLast ? (
                    <span className="text-sm text-[#1E293B] capitalize">{label}</span>
                  ) : (
                    <Link
                      to={routeTo}
                      className="text-sm text-gray-900 hover-text-main-600 capitalize"
                    >
                      {label}
                    </Link>
                  )}
                </span>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
    </>
  );
};

export default Breadcrumb;
